import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useKitchenStations } from '../../lib/admin-hooks';
import { useMenuCategories, useMenuItems, useMenuItemOptions } from '../../lib/menu-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { UtensilsCrossed, Plus, Edit2, Trash2, GripVertical, Layers, Settings2 } from 'lucide-react';
import { cn, formatCurrency } from '../../lib/utils';
import { MenuCategory, MenuItem, MenuItemOption } from '../../lib/types';
export function MenuManagementPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    categories,
    loading: catLoading,
    createCategory,
    updateCategory,
    toggleCategory
  } = useMenuCategories(restaurant?.id);
  const {
    items,
    loading: itemsLoading,
    createItem,
    updateItem,
    toggleItem,
    deleteItem
  } = useMenuItems(restaurant?.id);
  const {
    stations
  } = useKitchenStations(restaurant?.id);
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>('items');
  // Modals state
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<MenuCategory | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [managingOptionsForItem, setManagingOptionsForItem] = useState<MenuItem | null>(null);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1314">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-1315" />
      </div>;
  }
  const handleOpenCatModal = (cat?: MenuCategory) => {
    setEditingCat(cat || null);
    setIsCatModalOpen(true);
  };
  const handleOpenItemModal = (item?: MenuItem) => {
    setEditingItem(item || null);
    setIsItemModalOpen(true);
  };
  const handleOpenOptionsModal = (item: MenuItem) => {
    setManagingOptionsForItem(item);
    setIsOptionsModalOpen(true);
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1316">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-1317">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1318">
          <div data-id="element-1319">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1320">
              Gestión de Menú
            </h1>
            <p className="text-gray-500" data-id="element-1321">
              Administra categorías, productos y opciones
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1322">
            <Button variant={activeTab === 'categories' ? 'primary' : 'secondary'} onClick={() => setActiveTab('categories')} data-id="element-1323">
              <Layers className="w-4 h-4 mr-2" data-id="element-1324" />
              Categorías
            </Button>
            <Button variant={activeTab === 'items' ? 'primary' : 'secondary'} onClick={() => setActiveTab('items')} data-id="element-1325">
              <UtensilsCrossed className="w-4 h-4 mr-2" data-id="element-1326" />
              Productos
            </Button>
          </div>
        </div>

        {activeTab === 'categories' && <div className="space-y-4" data-id="element-1327">
            <div className="flex justify-end" data-id="element-1328">
              <Button onClick={() => handleOpenCatModal()} data-id="element-1329">
                <Plus className="w-4 h-4 mr-2" data-id="element-1330" />
                Nueva Categoría
              </Button>
            </div>

            {catLoading ? <div className="space-y-3" data-id="element-1331">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" data-id="element-1332" />)}
              </div> : categories.length === 0 ? <EmptyState icon={Layers} title="No hay categorías" description="Crea tu primera categoría para empezar a organizar tu menú." actionLabel="Crear Categoría" onAction={() => handleOpenCatModal()} data-id="element-1333" /> : <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1334">
                <ul className="divide-y divide-gray-100" data-id="element-1335">
                  {categories.map(cat => <li key={cat.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1336">
                      <div className="flex items-center gap-4" data-id="element-1337">
                        <GripVertical className="w-5 h-5 text-gray-300 cursor-grab" data-id="element-1338" />
                        <div data-id="element-1339">
                          <h3 className="font-bold text-gray-900" data-id="element-1340">
                            {cat.name}
                          </h3>
                          {cat.description && <p className="text-sm text-gray-500" data-id="element-1341">
                              {cat.description}
                            </p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1342">
                        <Badge variant={cat.is_active ? 'success' : 'default'} className="cursor-pointer" onClick={() => toggleCategory(cat.id, !cat.is_active)} data-id="element-1343">
                          {cat.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <button onClick={() => handleOpenCatModal(cat)} className="p-2 text-gray-400 hover:text-brand-amber transition-colors" data-id="element-1344">
                          <Edit2 className="w-4 h-4" data-id="element-1345" />
                        </button>
                      </div>
                    </li>)}
                </ul>
              </div>}
          </div>}

        {activeTab === 'items' && <div className="space-y-6" data-id="element-1346">
            <div className="flex justify-end" data-id="element-1347">
              <Button onClick={() => handleOpenItemModal()} data-id="element-1348">
                <Plus className="w-4 h-4 mr-2" data-id="element-1349" />
                Nuevo Producto
              </Button>
            </div>

            {itemsLoading || catLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1350">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" data-id="element-1351" />)}
              </div> : items.length === 0 ? <EmptyState icon={UtensilsCrossed} title="No hay productos" description="Agrega productos a tu menú para que los clientes puedan ordenar." actionLabel="Crear Producto" onAction={() => handleOpenItemModal()} data-id="element-1352" /> : <div className="space-y-8" data-id="element-1353">
                {categories.map(cat => {
            const catItems = items.filter(i => i.category_id === cat.id);
            if (catItems.length === 0) return null;
            return <div key={cat.id} className="space-y-4" data-id="element-1354">
                      <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1355">
                        {cat.name}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1356">
                        {catItems.map(item => <div key={item.id} className={cn('bg-white rounded-xl border p-4 flex flex-col justify-between transition-all', item.is_active ? 'border-gray-200 shadow-sm' : 'border-gray-200 opacity-60 bg-gray-50')} data-id="element-1357">
                            <div className="flex justify-between items-start mb-2" data-id="element-1358">
                              <div data-id="element-1359">
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-1360">
                                  {item.name}
                                </h3>
                                <p className="text-brand-amber font-bold" data-id="element-1361">
                                  {formatCurrency(item.price)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2" data-id="element-1362">
                                {item.station_code && <Badge variant="default" className="text-[10px] uppercase" data-id="element-1363">
                                    {item.station_code}
                                  </Badge>}
                                <Badge variant={item.is_active ? 'success' : 'default'} className="cursor-pointer" onClick={() => toggleItem(item.id, !item.is_active)} data-id="element-1364">
                                  {item.is_active ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </div>
                            </div>
                            {item.description && <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-1365">
                                {item.description}
                              </p>}
                            <div className="flex justify-end gap-2 mt-auto pt-4 border-t border-gray-100" data-id="element-1366">
                              <Button variant="secondary" size="sm" onClick={() => handleOpenOptionsModal(item)} data-id="element-1367">
                                <Settings2 className="w-4 h-4 mr-1.5" data-id="element-1368" />
                                Opciones
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleOpenItemModal(item)} data-id="element-1369">
                                <Edit2 className="w-4 h-4 mr-1.5" data-id="element-1370" />
                                Editar
                              </Button>
                            </div>
                          </div>)}
                      </div>
                    </div>;
          })}
              </div>}
          </div>}
      </div>

      {/* Modals */}
      <CategoryModal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} category={editingCat} onSave={async data => {
      let result;
      if (editingCat) {
        result = await updateCategory(editingCat.id, data);
      } else {
        result = await createCategory(data);
      }
      if (result?.success) {
        setIsCatModalOpen(false);
      }
      return result;
    }} data-id="element-1371" />

      <ItemModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} item={editingItem} categories={categories} stations={stations} onSave={async data => {
      let result;
      if (editingItem) {
        result = await updateItem(editingItem.id, data);
      } else {
        result = await createItem(data as any);
      }
      if (result?.success) {
        setIsItemModalOpen(false);
      }
      return result;
    }} onDelete={async () => {
      if (editingItem) {
        if (confirm('¿Estás seguro de eliminar este producto?')) {
          const result = await deleteItem(editingItem.id);
          if (result?.success) {
            setIsItemModalOpen(false);
          }
          return result;
        }
      }
      return {
        success: false
      };
    }} data-id="element-1372" />

      {managingOptionsForItem && <OptionsModal isOpen={isOptionsModalOpen} onClose={() => setIsOptionsModalOpen(false)} item={managingOptionsForItem} restaurantId={restaurant.id} data-id="element-1373" />}
    </AdminLayout>;
}
// ─── Modals ──────────────────────────────────────────────────
function CategoryModal({
  isOpen,
  onClose,
  category,
  onSave
}: {
  isOpen: boolean;
  onClose: () => void;
  category: MenuCategory | null;
  onSave: (data: any) => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setName(category?.name || '');
      setDescription(category?.description || '');
      setIsActive(category?.is_active ?? true);
      setError(null);
    }
  }, [isOpen, category]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    console.log('[NOIZA][UI] CategoryModal handleSubmit called with:', {
      name,
      description,
      is_active: isActive
    });
    try {
      const result = await onSave({
        name,
        description,
        is_active: isActive
      });
      console.log('[NOIZA][UI] CategoryModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar la categoría');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] CategoryModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={category ? 'Editar Categoría' : 'Nueva Categoría'} data-id="element-1374">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1375">
        <Input label="Nombre" value={name} onChange={e => setName(e.target.value)} required placeholder="Ej: Entradas, Bebidas, Postres" data-id="element-1376" />
        <Textarea label="Descripción (opcional)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Breve descripción de la categoría" data-id="element-1377" />
        <div className="flex items-center gap-2 pt-2" data-id="element-1378">
          <input type="checkbox" id="cat-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-1379" />
          <label htmlFor="cat-active" className="text-sm font-medium text-gray-700" data-id="element-1380">
            Categoría activa (visible para clientes)
          </label>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-1381">
            {error}
          </div>}
        <div className="pt-4 flex justify-end gap-3" data-id="element-1382">
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-1383">
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} data-id="element-1384">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>;
}
function ItemModal({
  isOpen,
  onClose,
  item,
  categories,
  stations,
  onSave,
  onDelete
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  categories: MenuCategory[];
  stations: any[];
  onSave: (data: any) => Promise<any>;
  onDelete: () => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stationCode, setStationCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setName(item?.name || '');
      setDescription(item?.description || '');
      setPrice(item?.price?.toString() || '');
      setCategoryId(item?.category_id || (categories[0]?.id ?? ''));
      setStationCode(item?.station_code || '');
      setIsActive(item?.is_active ?? true);
      setError(null);
    }
  }, [isOpen, item, categories]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const payload = {
      name,
      description,
      price: parseFloat(price),
      category_id: categoryId,
      station_code: stationCode || null,
      is_active: isActive
    };
    console.log('[NOIZA][UI] ItemModal handleSubmit called with:', payload);
    try {
      const result = await onSave(payload);
      console.log('[NOIZA][UI] ItemModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar el producto');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] ItemModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Editar Producto' : 'Nuevo Producto'} data-id="element-1385">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1386">
        <Input label="Nombre del producto" value={name} onChange={e => setName(e.target.value)} required data-id="element-1387" />
        <div className="grid grid-cols-2 gap-4" data-id="element-1388">
          <Input label="Precio" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} required data-id="element-1389" />
          <div data-id="element-1390">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1391">
              Categoría
            </label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} required className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber" data-id="element-1392">
              <option value="" disabled data-id="element-1393">
                Seleccionar...
              </option>
              {categories.map(c => <option key={c.id} value={c.id} data-id="element-1394">
                  {c.name}
                </option>)}
            </select>
          </div>
        </div>

        <div data-id="element-1395">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1396">
            Estación de preparación (KDS)
          </label>
          <select value={stationCode} onChange={e => setStationCode(e.target.value)} className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber" data-id="element-1397">
            <option value="" data-id="element-1398">Sin estación específica</option>
            {stations.map(s => <option key={s.id} value={s.code} data-id="element-1399">
                {s.name}
              </option>)}
          </select>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1400">
            Determina en qué pantalla de cocina aparecerá este producto.
          </p>
        </div>

        <Textarea label="Descripción" value={description} onChange={e => setDescription(e.target.value)} data-id="element-1401" />

        <div className="flex items-center gap-2 pt-2" data-id="element-1402">
          <input type="checkbox" id="item-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-1403" />
          <label htmlFor="item-active" className="text-sm font-medium text-gray-700" data-id="element-1404">
            Producto activo (visible para clientes)
          </label>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-1405">
            {error}
          </div>}
        <div className="pt-4 flex justify-between items-center" data-id="element-1406">
          {item ? <Button type="button" variant="danger" onClick={onDelete} data-id="element-1407">
              Eliminar
            </Button> : <div data-id="element-1408" />}
          <div className="flex gap-3" data-id="element-1409">
            <Button type="button" variant="ghost" onClick={onClose} data-id="element-1410">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-1411">
              Guardar
            </Button>
          </div>
        </div>
      </form>
    </Modal>;
}
function OptionsModal({
  isOpen,
  onClose,
  item,
  restaurantId
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  restaurantId: string;
}) {
  const {
    options,
    loading,
    createOption,
    deleteOption
  } = useMenuItemOptions(item.id);
  const [name, setName] = useState('');
  const [priceDelta, setPriceDelta] = useState('');
  const [group, setGroup] = useState('Opciones');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await createOption({
      restaurant_id: restaurantId,
      option_group: group,
      name,
      price_delta: parseFloat(priceDelta) || 0
    });
    setName('');
    setPriceDelta('');
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Opciones: ${item.name}`} data-id="element-1412">
      <div className="space-y-6" data-id="element-1413">
        {/* List existing options */}
        {loading ? <Skeleton className="h-20 w-full rounded-xl" data-id="element-1414" /> : options.length > 0 ? <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden" data-id="element-1415">
            <ul className="divide-y divide-gray-200" data-id="element-1416">
              {options.map(opt => <li key={opt.id} className="p-3 flex justify-between items-center" data-id="element-1417">
                  <div data-id="element-1418">
                    <p className="font-medium text-sm text-gray-900" data-id="element-1419">
                      {opt.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1420">
                      Grupo: {opt.option_group}
                    </p>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1421">
                    <span className="text-sm font-bold text-gray-900" data-id="element-1422">
                      {opt.price_delta > 0 ? '+' : ''}
                      {formatCurrency(opt.price_delta)}
                    </span>
                    <button onClick={() => deleteOption(opt.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-1423">
                      <Trash2 className="w-4 h-4" data-id="element-1424" />
                    </button>
                  </div>
                </li>)}
            </ul>
          </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-1425">
            Este producto no tiene opciones adicionales.
          </p>}

        {/* Add new option form */}
        <form onSubmit={handleAdd} className="bg-white p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-1426">
          <h4 className="font-bold text-sm text-gray-900" data-id="element-1427">Agregar Opción</h4>
          <div className="grid grid-cols-2 gap-3" data-id="element-1428">
            <Input placeholder="Nombre (ej: Extra Queso)" value={name} onChange={e => setName(e.target.value)} required data-id="element-1429" />
            <Input type="number" step="0.01" placeholder="Precio extra (ej: 500)" value={priceDelta} onChange={e => setPriceDelta(e.target.value)} required data-id="element-1430" />
          </div>
          <Input placeholder="Grupo (ej: Extras, Tamaños)" value={group} onChange={e => setGroup(e.target.value)} required data-id="element-1431" />
          <Button type="submit" fullWidth isLoading={isSubmitting} size="sm" data-id="element-1432">
            Agregar Opción
          </Button>
        </form>
      </div>
    </Modal>;
}